import { MAP_VOID_TILE, type MapData, type StaticPlacement } from './map';
import type { TilePoint } from './mobile';
import { SeededRng } from './rng';
import {
  DUNGEON_MODULES,
  deriveDungeonClearanceHalo,
  dungeonRoomSizeClassForDimensions,
  transformDungeonPoint,
  transformDungeonFootprint,
  transformDungeonPoints,
  transformDungeonRegion,
  transformedDungeonSocket,
  validateDungeonModule,
  type DungeonModuleDefinition,
  type DungeonModuleTransform,
  type DungeonRoomSizeClass,
} from './dungeonModule';
import { layoutDungeonFootprints } from './dungeonFootprintLayout';
import {
  planDungeonTopology,
  type DungeonTopology,
  type DungeonTopologyRoom as TopologyRoom,
} from './dungeonTopology';
import { compileDungeonStructures } from './dungeonStructureCompiler';
import {
  routeDungeonCorridor,
  type DungeonCorridorSegment as RoutedDungeonCorridorSegment,
} from './dungeonCorridorRouter';
import {
  CLASSIC_STONE_STRUCTURE_KIT_V2,
  defineDungeonStructureKit,
  dungeonStructureKit,
  dungeonStructurePieceForRole,
  dungeonStructureTerrainAction,
  type DungeonStructurePiece,
  type DungeonStructureKit,
} from './dungeonStructureKit';

export const DUNGEON_GENERATOR_VERSION = 9;
export const DUNGEON_MIN_ROOMS = 15;
export const DUNGEON_MAX_ROOMS = 20;
export const DUNGEON_MAX_ATTEMPTS = 32;
export const DUNGEON_MAX_MODULES = 256;
export const DUNGEON_MAX_MAP_PAYLOAD_BYTES = 3_500_000;
export const DUNGEON_MAX_RENDER_PLACEMENTS = 50_000;

export type DungeonDirection = 'north' | 'east' | 'south' | 'west';
export type DungeonRoomRole =
  | 'entrance'
  | 'exit'
  | 'landmark'
  | 'junction'
  | 'dead-end'
  | 'ordinary';

export interface DungeonSocketRef {
  roomId: string;
  socketId?: string;
  direction: DungeonDirection;
  position: TilePoint;
  compatibility?: string;
}

export interface DungeonCorridorSegment extends RoutedDungeonCorridorSegment {
  moduleId: string;
  transform: DungeonModuleTransform;
  region: DungeonRegion;
}

export interface DungeonConnection {
  id: string;
  moduleId: string;
  passageWidth: number;
  region: DungeonRegion;
  transform: DungeonModuleTransform;
  a: DungeonSocketRef;
  b: DungeonSocketRef;
  revealRegion: DungeonRegion;
  /** Exact independently revealable route pieces. Legacy blueprints use `revealRegion`. */
  revealRegions?: DungeonRegion[];
  segments?: DungeonCorridorSegment[];
  footprint?: TilePoint[];
  clearanceHalo?: TilePoint[];
  door?: TilePoint;
}

export interface DungeonRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DungeonRoom {
  id: string;
  moduleId: string;
  role: DungeonRoomRole;
  grid: TilePoint;
  region: DungeonRegion;
  revealRegion: DungeonRegion;
  /** Exact authored reveal shape. Legacy blueprints fall back to `revealRegion`. */
  revealRegions?: DungeonRegion[];
  /** Exact transformed semantic floor, independent of the storage envelope. */
  footprint?: TilePoint[];
  /** Exact transformed non-floor ring reserved around this room. */
  clearanceHalo?: TilePoint[];
  sizeClass?: DungeonRoomSizeClass;
  reservedZones: DungeonRegion[];
  encounterZones?: DungeonRegion[];
  transform: DungeonModuleTransform;
  landmark?: string;
}

export interface DungeonMarker {
  roomId: string;
  position: TilePoint;
}

export interface AuthoredDungeonSource {
  kind: 'authored';
  id: string;
  revision: number;
}

export interface DungeonBlueprint {
  /**
   * Absent means the procedural seed and generator version are the canonical
   * replay identity. Authored spaces carry their own immutable identity.
   */
  source?: AuthoredDungeonSource;
  /** Distinguishes independently versioned procedural generator families. */
  generatorId?: string;
  seed: number;
  generatorVersion: number;
  rooms: DungeonRoom[];
  connections: DungeonConnection[];
  entrance: DungeonMarker;
  exit: DungeonMarker;
  /** Defaults true for procedural adventures. */
  showExitOnAutomap?: boolean;
  landmarks: DungeonMarker[];
}

/** Return the canonical identity used for scene caches, run ids, and telemetry. */
export function dungeonSourceKey(blueprint: DungeonBlueprint): string {
  return blueprint.source
    ? `authored:${blueprint.source.id}:${blueprint.source.revision}`
    : blueprint.generatorId
      ? `generated:${blueprint.generatorId}:${blueprint.generatorVersion}:${blueprint.seed}`
      : `generated:${blueprint.generatorVersion}:${blueprint.seed}`;
}

export interface DungeonMetrics {
  mainRouteDepth: number;
  mainRouteTurns: number;
  mainRouteBranchCount: number;
  branchCount: number;
  independentLoops: number;
  shortestLoopLength: number;
  deadEndCount: number;
  maxDeadEndDepth: number;
  rewardedDeadEndCount: number;
  minLandmarkSpacing: number;
  backtrackingBurden: number;
  packingBacktracks: number;
  moduleRepetition: number;
  compactness: number;
  unrelatedAxisAlignment: number;
}

export interface GeneratedDungeon {
  ok: true;
  seed: number;
  generatorVersion: number;
  attempts: number;
  map: MapData;
  blueprint: DungeonBlueprint;
  metrics: DungeonMetrics;
}

export interface DungeonGenerationFailure {
  ok: false;
  seed: number;
  generatorVersion: number;
  attempts: number;
  constraint: string;
  stage?: 'config' | 'modules' | 'topology' | 'packing' | 'assembly';
  diagnostics?: Record<string, number | string>;
}

export interface DungeonGenerationConfig {
  minRooms: number;
  maxRooms: number;
  maxAttempts: number;
  minLoops: number;
  minLoopLength: number;
  minMainRouteDepth: number;
  minMainRouteTurns: number;
  maxTopologyAspectRatio: number;
  maxTopologyCompactness: number;
  minBranches: number;
  minDeadEnds: number;
  minDeadEndDepth: number;
  minRewardedDeadEnds: number;
  maxUnrelatedAxisAlignment: number;
}

export interface GenerateDungeonOptions {
  seed: number;
  generatorVersion?: number;
}

export interface GenerateDungeonCandidateOptions extends GenerateDungeonOptions {
  /** Authoring-only constraint overrides for failure-path and corpus probes. */
  config?: Partial<DungeonGenerationConfig>;
  /** Injectable editor-authored library for validation and generator tests. */
  moduleLibrary?: readonly DungeonModuleDefinition[];
  /** Authoring-only themed art adapter; production uses the registered current kit. */
  structureKit?: DungeonStructureKit;
}

interface PlannedRoom extends TopologyRoom {
  x: number;
  y: number;
}

interface PackedDungeonTopology extends DungeonTopology {
  rooms: PlannedRoom[];
  backtracks: number;
}

const DEFAULT_CONFIG: DungeonGenerationConfig = {
  minRooms: DUNGEON_MIN_ROOMS,
  maxRooms: DUNGEON_MAX_ROOMS,
  maxAttempts: DUNGEON_MAX_ATTEMPTS,
  minLoops: 1,
  minLoopLength: 6,
  minMainRouteDepth: 6,
  minMainRouteTurns: 2,
  maxTopologyAspectRatio: 2.5,
  maxTopologyCompactness: 0.85,
  minBranches: 2,
  minDeadEnds: 2,
  minDeadEndDepth: 2,
  minRewardedDeadEnds: 2,
  maxUnrelatedAxisAlignment: 0.25,
};

const DIRECTIONS: ReadonlyArray<{
  direction: DungeonDirection;
  opposite: DungeonDirection;
  dx: number;
  dy: number;
}> = [
  { direction: 'north', opposite: 'south', dx: 0, dy: -1 },
  { direction: 'east', opposite: 'west', dx: 1, dy: 0 },
  { direction: 'south', opposite: 'north', dx: 0, dy: 1 },
  { direction: 'west', opposite: 'east', dx: -1, dy: 0 },
];

const FLOOR_TILES = ['land-0x4eb', 'land-0x4ec', 'land-0x4ed', 'land-0x4ee'];
/**
 * A deterministic weighted bag. Most passages stay near ten tiles wide, while
 * every generated dungeon also gets occasional broad combat lanes.
 */
export const DUNGEON_PASSAGE_WIDTHS = [
  9, 9, 9, 9, 9,
  11, 11, 11,
  13, 13,
  15,
  17,
  21,
  25,
] as const;
export const DUNGEON_HALLWAY_LENGTHS = [27, 33, 39, 45, 51, 57] as const;
const MAP_MARGIN = 2;
const PACKING_MAX_BACKTRACKS = 20_000;
const MODULE_SELECTION_MAX_BACKTRACKS = 20_000;

interface DungeonGenerationContract {
  generatorVersion: number;
  passageWidths: readonly number[];
  hallwayLengths: readonly number[];
  structureKitId?: string;
  /** Candidate-only adapter. Production contracts resolve structureKitId through the registry. */
  structureKit?: DungeonStructureKit;
}

const CURRENT_DUNGEON_CONTRACT: DungeonGenerationContract = {
  generatorVersion: DUNGEON_GENERATOR_VERSION,
  passageWidths: DUNGEON_PASSAGE_WIDTHS,
  hallwayLengths: DUNGEON_HALLWAY_LENGTHS,
  structureKitId: CLASSIC_STONE_STRUCTURE_KIT_V2.id,
};

function applyStructureTerrain(
  piece: DungeonStructurePiece | null,
  cells: number[],
  cell: number,
  paletteIndexByName: ReadonlyMap<string, number>,
): void {
  const action = dungeonStructureTerrainAction(piece, cells[cell] === 0);
  if (action?.kind === 'void') {
    cells[cell] = 0;
    return;
  }
  if (action?.kind === 'fill') {
    cells[cell] = paletteIndexByName.get(action.tileName)!;
  }
}

function key(x: number, y: number): string {
  return `${x},${y}`;
}

function edgeKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

function mixedSeed(seed: number, attempt: number): number {
  let value = (seed ^ Math.imul(attempt + 1, 0x9e3779b1)) >>> 0;
  value ^= value >>> 16;
  value = Math.imul(value, 0x85ebca6b) >>> 0;
  value ^= value >>> 13;
  return value >>> 0;
}

function shuffled<T>(values: readonly T[], rng: SeededRng): T[] {
  const copy = [...values];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = rng.nextInt(i + 1);
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy;
}

function adjacency(topology: Pick<DungeonTopology, 'rooms' | 'edges'>): Map<string, string[]> {
  const result = new Map(topology.rooms.map((room) => [room.id, [] as string[]]));
  for (const edge of topology.edges) {
    result.get(edge.a)!.push(edge.b);
    result.get(edge.b)!.push(edge.a);
  }
  return result;
}

function distances(graph: Map<string, string[]>, start: string): Map<string, number> {
  const result = new Map([[start, 0]]);
  const pending = [start];
  while (pending.length > 0) {
    const current = pending.shift()!;
    const distance = result.get(current)!;
    for (const next of graph.get(current) ?? []) {
      if (result.has(next)) continue;
      result.set(next, distance + 1);
      pending.push(next);
    }
  }
  return result;
}

function shortestPath(
  graph: ReadonlyMap<string, readonly string[]>,
  start: string,
  goal: string,
): string[] {
  const previous = new Map<string, string | null>([[start, null]]);
  const pending = [start];
  while (pending.length > 0) {
    const current = pending.shift()!;
    if (current === goal) break;
    for (const next of graph.get(current) ?? []) {
      if (previous.has(next)) continue;
      previous.set(next, current);
      pending.push(next);
    }
  }
  const result: string[] = [];
  for (let current: string | null = goal; current !== null; current = previous.get(current) ?? null) {
    result.push(current);
  }
  return result.reverse();
}

function coordinateCandidates(
  roomId: string,
  graph: Map<string, string[]>,
  placed: Map<string, TilePoint>,
  rng: SeededRng,
): TilePoint[] {
  const placedNeighbors = (graph.get(roomId) ?? [])
    .map((id) => placed.get(id))
    .filter((point): point is TilePoint => point !== undefined);
  if (placedNeighbors.length === 0) return [];
  const first = new Map<string, TilePoint>();
  for (const direction of shuffled(DIRECTIONS, rng)) {
    const point = {
      x: placedNeighbors[0]!.x + direction.dx,
      y: placedNeighbors[0]!.y + direction.dy,
    };
    first.set(key(point.x, point.y), point);
  }
  for (const neighbor of placedNeighbors.slice(1)) {
    for (const [candidateKey, candidate] of first) {
      if (Math.abs(candidate.x - neighbor.x) + Math.abs(candidate.y - neighbor.y) !== 1) {
        first.delete(candidateKey);
      }
    }
  }
  return [...first.values()];
}

/** Place the already-chosen graph with bounded deterministic backtracking. */
function packTopology(topology: DungeonTopology, rng: SeededRng): PackedDungeonTopology | null {
  const graph = adjacency(topology);
  const occupied = new Set<string>();
  const placed = new Map<string, TilePoint>([[topology.entranceId, { x: 0, y: 0 }]]);
  occupied.add(key(0, 0));
  let backtracks = 0;

  const placeNext = (): boolean => {
    if (placed.size === topology.rooms.length) return true;
    if (backtracks >= PACKING_MAX_BACKTRACKS) return false;
    const candidates = topology.rooms
      .filter((room) => !placed.has(room.id))
      .map((room) => ({
        room,
        placedNeighbors: (graph.get(room.id) ?? []).filter((neighbor) => placed.has(neighbor)).length,
      }))
      .filter((candidate) => candidate.placedNeighbors > 0)
      .sort((a, b) => b.placedNeighbors - a.placedNeighbors || a.room.id.localeCompare(b.room.id));
    const next = candidates[0]?.room;
    if (!next) return false;
    for (const point of coordinateCandidates(next.id, graph, placed, rng)) {
      const pointKey = key(point.x, point.y);
      if (occupied.has(pointKey)) continue;
      const freeSides = DIRECTIONS.filter(
        (direction) => !occupied.has(key(point.x + direction.dx, point.y + direction.dy)),
      ).length;
      const unplacedNeighbors = (graph.get(next.id) ?? []).filter((neighbor) => !placed.has(neighbor)).length;
      if (freeSides < unplacedNeighbors) continue;
      placed.set(next.id, point);
      occupied.add(pointKey);
      if (placeNext()) return true;
      placed.delete(next.id);
      occupied.delete(pointKey);
      backtracks += 1;
      if (backtracks >= PACKING_MAX_BACKTRACKS) return false;
    }
    return false;
  };

  if (!placeNext()) return null;
  return {
    ...topology,
    rooms: topology.rooms.map((room) => ({ ...room, ...placed.get(room.id)! })),
    backtracks,
  };
}

function packedTopologyFailure(
  topology: PackedDungeonTopology,
  config: DungeonGenerationConfig,
): string | null {
  const graph = adjacency(topology);
  const roomById = new Map(topology.rooms.map((room) => [room.id, room]));
  const route = shortestPath(graph, topology.entranceId, topology.exitId);
  let turns = 0;
  let previousAxis: 'horizontal' | 'vertical' | undefined;
  for (let index = 1; index < route.length; index++) {
    const previous = roomById.get(route[index - 1]!)!;
    const current = roomById.get(route[index]!)!;
    const axis = previous.x === current.x ? 'vertical' : 'horizontal';
    if (previousAxis !== undefined && previousAxis !== axis) turns += 1;
    previousAxis = axis;
  }
  if (turns < config.minMainRouteTurns) {
    return `main-route-turns ${turns} < ${config.minMainRouteTurns}`;
  }

  const minX = Math.min(...topology.rooms.map((room) => room.x));
  const maxX = Math.max(...topology.rooms.map((room) => room.x));
  const minY = Math.min(...topology.rooms.map((room) => room.y));
  const maxY = Math.max(...topology.rooms.map((room) => room.y));
  const spanX = maxX - minX + 1;
  const spanY = maxY - minY + 1;
  const aspectRatio = Math.max(spanX, spanY) / Math.min(spanX, spanY);
  if (aspectRatio > config.maxTopologyAspectRatio) {
    return `topology-aspect-ratio ${aspectRatio} > ${config.maxTopologyAspectRatio}`;
  }
  const compactness = topology.rooms.length / (spanX * spanY);
  if (compactness > config.maxTopologyCompactness) {
    return `topology-compactness ${compactness} > ${config.maxTopologyCompactness}`;
  }
  return null;
}

function topologyFailure(topology: DungeonTopology, config: DungeonGenerationConfig): string | null {
  const graph = adjacency(topology);
  if (graph.size !== topology.rooms.length) return 'room-adjacency-missing';
  if (distances(graph, topology.entranceId).size !== topology.rooms.length) {
    return 'unreachable-room';
  }
  const loops = topology.edges.length - topology.rooms.length + 1;
  if (loops < config.minLoops) return `independent-loops ${loops} < ${config.minLoops}`;
  const loopLength = shortestCycleLength(graph);
  if (loopLength < config.minLoopLength) {
    return `shortest-loop-length ${loopLength} < ${config.minLoopLength}`;
  }
  const depth = distances(graph, topology.entranceId).get(topology.exitId) ?? -1;
  if (depth < config.minMainRouteDepth) {
    return `main-route-depth ${depth} < ${config.minMainRouteDepth}`;
  }
  const branches = [...graph.values()].filter((neighbors) => neighbors.length >= 3).length;
  if (branches < config.minBranches) return `branches ${branches} < ${config.minBranches}`;
  const deadEnds = [...graph]
    .filter(([id, neighbors]) =>
      id !== topology.entranceId && id !== topology.exitId && neighbors.length === 1,
    ).length;
  if (deadEnds < config.minDeadEnds) return `dead-ends ${deadEnds} < ${config.minDeadEnds}`;
  const sideDepths = deadEndDepths(graph, topology.entranceId, topology.exitId);
  const shallowest = Math.min(...sideDepths.values());
  if (shallowest < config.minDeadEndDepth) {
    return `dead-end-depth ${shallowest} < ${config.minDeadEndDepth}`;
  }
  const rewarded = topology.rooms.filter(
    (room) => room.role === 'landmark' && (sideDepths.get(room.id) ?? 0) >= config.minDeadEndDepth,
  ).length;
  if (rewarded < config.minRewardedDeadEnds) {
    return `rewarded-dead-ends ${rewarded} < ${config.minRewardedDeadEnds}`;
  }
  return null;
}

function directionBetween(a: PlannedRoom, b: PlannedRoom): DungeonDirection {
  if (b.x === a.x && b.y === a.y - 1) return 'north';
  if (b.x === a.x + 1 && b.y === a.y) return 'east';
  if (b.x === a.x && b.y === a.y + 1) return 'south';
  if (b.x === a.x - 1 && b.y === a.y) return 'west';
  throw new Error(`rooms ${a.id} and ${b.id} are not spatial neighbors`);
}

function opposite(direction: DungeonDirection): DungeonDirection {
  return DIRECTIONS.find((candidate) => candidate.direction === direction)!.opposite;
}

function contains(region: DungeonRegion, point: TilePoint): boolean {
  return (
    point.x >= region.x &&
    point.y >= region.y &&
    point.x < region.x + region.width &&
    point.y < region.y + region.height
  );
}

function floorCell(x: number, y: number, seed: number): number {
  const hash = (Math.imul(x + 17, 73856093) ^ Math.imul(y + 31, 19349663) ^ seed) >>> 0;
  return 1 + (hash % FLOOR_TILES.length);
}

interface ModuleAssignment {
  module: DungeonModuleDefinition;
  transform: DungeonModuleTransform;
}

function weightedModuleCandidates(
  role: DungeonRoomRole,
  rng: SeededRng,
  moduleLibrary: readonly DungeonModuleDefinition[],
): ModuleAssignment[] {
  const candidates = moduleLibrary
    .filter((module) => module.category === 'room' && module.roles.includes(role))
    .flatMap((module) => module.allowedTransforms.map((transform) => ({ module, transform })));
  // Weighted random permutation without expanding `weight` copies. Candidate
  // construction is O(modules * transforms), independent of authored weights.
  return candidates
    .map((candidate) => ({
      candidate,
      priority:
        Math.log(Math.max(Number.MIN_VALUE, rng.next())) / candidate.module.weight,
    }))
    .sort((a, b) => b.priority - a.priority)
    .map(({ candidate }) => candidate);
}

function socketInDirection(
  assignment: ModuleAssignment,
  direction: DungeonDirection,
) {
  return assignment.module.sockets
    .map((socket) => transformedDungeonSocket(assignment.module, socket, assignment.transform))
    .find((socket) => socket.direction === direction);
}

function corridorAssignment(
  direction: DungeonDirection,
  compatibility: string,
  rng: SeededRng,
  moduleLibrary: readonly DungeonModuleDefinition[],
): ModuleAssignment | undefined {
  const candidates = moduleLibrary
    .filter((module) => module.category === 'corridor')
    .flatMap((module) => module.allowedTransforms.map((transform) => ({ module, transform })))
    .filter((candidate) => {
      const requiredDirection =
        direction === 'east' || direction === 'west' ? 'east-west' : 'north-south';
      return (
        candidate.transform.rotation === 0 &&
        !candidate.transform.mirrored &&
        candidate.module.repeat?.direction === requiredDirection &&
        candidate.module.repeat.compatibility === compatibility
      );
    });
  return shuffled(candidates, rng)[0];
}

/** Backtrack over module and transform choices after spatial packing fixes edge directions. */
function selectModuleAssignments(
  topology: PackedDungeonTopology,
  roles: Map<string, DungeonRoomRole>,
  rng: SeededRng,
  moduleLibrary: readonly DungeonModuleDefinition[],
): { assignments: Map<string, ModuleAssignment>; backtracks: number } | null {
  const graph = adjacency(topology);
  const roomById = new Map(topology.rooms.map((room) => [room.id, room]));
  const candidates = new Map(
    topology.rooms.map((room) => [
      room.id,
      weightedModuleCandidates(roles.get(room.id)!, rng, moduleLibrary).filter((candidate) =>
        (graph.get(room.id) ?? []).every((neighborId) =>
          socketInDirection(candidate, directionBetween(room, roomById.get(neighborId)!)),
        ),
      ),
    ]),
  );
  const assignments = new Map<string, ModuleAssignment>();
  let backtracks = 0;
  const assignNext = (): boolean => {
    if (assignments.size === topology.rooms.length) return true;
    if (backtracks >= MODULE_SELECTION_MAX_BACKTRACKS) return false;
    const next = topology.rooms
      .filter((room) => !assignments.has(room.id))
      .sort((a, b) => {
        const aPlaced = (graph.get(a.id) ?? []).filter((id) => assignments.has(id)).length;
        const bPlaced = (graph.get(b.id) ?? []).filter((id) => assignments.has(id)).length;
        return bPlaced - aPlaced || (candidates.get(a.id)?.length ?? 0) - (candidates.get(b.id)?.length ?? 0);
      })[0];
    if (!next) return false;
    for (const candidate of candidates.get(next.id) ?? []) {
      const compatible = (graph.get(next.id) ?? []).every((neighborId) => {
        const neighbor = assignments.get(neighborId);
        if (!neighbor) return true;
        const direction = directionBetween(next, roomById.get(neighborId)!);
        const ownSocket = socketInDirection(candidate, direction);
        const neighborSocket = socketInDirection(neighbor, opposite(direction));
        return (
          ownSocket !== undefined &&
          neighborSocket !== undefined &&
          ownSocket.width === neighborSocket.width &&
          ownSocket.compatibility === neighborSocket.compatibility
        );
      });
      if (!compatible) continue;
      assignments.set(next.id, candidate);
      if (assignNext()) return true;
      assignments.delete(next.id);
      backtracks += 1;
    }
    return false;
  };
  return assignNext() ? { assignments, backtracks } : null;
}

function globalPoint(region: DungeonRegion, point: TilePoint): TilePoint {
  return { x: region.x + point.x, y: region.y + point.y };
}

function globalRegion(region: DungeonRegion, local: DungeonRegion): DungeonRegion {
  return { x: region.x + local.x, y: region.y + local.y, width: local.width, height: local.height };
}

function cellsInRegion(region: DungeonRegion): TilePoint[] {
  const points: TilePoint[] = [];
  for (let y = region.y; y < region.y + region.height; y++) {
    for (let x = region.x; x < region.x + region.width; x++) points.push({ x, y });
  }
  return points;
}

function unionRegions(regions: DungeonRegion[]): DungeonRegion {
  const minX = Math.min(...regions.map((region) => region.x));
  const minY = Math.min(...regions.map((region) => region.y));
  const maxX = Math.max(...regions.map((region) => region.x + region.width));
  const maxY = Math.max(...regions.map((region) => region.y + region.height));
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function repeatableHallwayPoints(
  module: DungeonModuleDefinition,
  point: TilePoint,
  segment: RoutedDungeonCorridorSegment,
): TilePoint[] {
  if (!module.repeat) return [];
  const horizontal = segment.direction === 'east' || segment.direction === 'west';
  const length = Math.abs(segment.to.x - segment.from.x) + Math.abs(segment.to.y - segment.from.y);
  const dx = segment.direction === 'east' ? 1 : segment.direction === 'west' ? -1 : 0;
  const dy = segment.direction === 'south' ? 1 : segment.direction === 'north' ? -1 : 0;
  const crossOffset = horizontal
    ? point.y - Math.floor(module.height / 2)
    : point.x - Math.floor(module.width / 2);
  return Array.from({ length: length + 1 }, (_, offset) => ({
    x: segment.from.x + dx * offset + (horizontal ? 0 : crossOffset),
    y: segment.from.y + dy * offset + (horizontal ? crossOffset : 0),
  }));
}

function deadEndDepths(
  graph: Map<string, string[]>,
  entranceId: string,
  exitId: string,
): Map<string, number> {
  const depths = new Map<string, number>();
  for (const [start, neighbors] of graph) {
    if (start === entranceId || start === exitId || neighbors.length !== 1) continue;
    let previous = start;
    let current = neighbors[0]!;
    let depth = 1;
    while ((graph.get(current) ?? []).length === 2) {
      const next = graph.get(current)!.find((neighbor) => neighbor !== previous)!;
      previous = current;
      current = next;
      depth += 1;
    }
    depths.set(start, depth);
  }
  return depths;
}

function shortestCycleLength(graph: ReadonlyMap<string, readonly string[]>): number {
  let shortest = Number.POSITIVE_INFINITY;
  for (const [a, neighbors] of graph) {
    for (const b of neighbors) {
      if (a >= b) continue;
      const reached = new Map([[a, 0]]);
      const pending = [a];
      while (pending.length > 0) {
        const current = pending.shift()!;
        for (const next of graph.get(current) ?? []) {
          if ((current === a && next === b) || (current === b && next === a)) continue;
          if (reached.has(next)) continue;
          reached.set(next, reached.get(current)! + 1);
          pending.push(next);
        }
      }
      const alternate = reached.get(b);
      if (alternate !== undefined) shortest = Math.min(shortest, alternate + 1);
    }
  }
  return shortest;
}

function assemble(
  topology: PackedDungeonTopology,
  seed: number,
  attempts: number,
  moduleLibrary: readonly DungeonModuleDefinition[],
  contract: DungeonGenerationContract,
  config: DungeonGenerationConfig,
): GeneratedDungeon {
  const decorationRng = new SeededRng(mixedSeed(seed, 0x5a17 + attempts));
  const structureKit = contract.structureKit ?? (
    contract.structureKitId ? dungeonStructureKit(contract.structureKitId) : undefined
  );
  if (contract.structureKitId && !structureKit) {
    throw new Error(`assembly:missing-structure-kit-${contract.structureKitId}`);
  }
  const minX = Math.min(...topology.rooms.map((room) => room.x));
  const maxX = Math.max(...topology.rooms.map((room) => room.x));
  const minY = Math.min(...topology.rooms.map((room) => room.y));
  const maxY = Math.max(...topology.rooms.map((room) => room.y));
  const graph = adjacency(topology);
  const entranceDistances = distances(graph, topology.entranceId);
  const roles = new Map(topology.rooms.map((room) => [room.id, room.role]));
  const moduleSelection = selectModuleAssignments(topology, roles, decorationRng, moduleLibrary);
  if (!moduleSelection) throw new Error('packing:compatible-module-budget-exhausted');

  const layoutRng = new SeededRng(mixedSeed(seed, 0x4c41594f + attempts));
  const layout = layoutDungeonFootprints({
    rooms: topology.rooms.map((room) => {
      const assignment = moduleSelection.assignments.get(room.id)!;
      const bounds = transformDungeonRegion(
        assignment.module,
        { x: 0, y: 0, width: assignment.module.width, height: assignment.module.height },
        assignment.transform,
      );
      return { id: room.id, x: room.x, y: room.y, width: bounds.width, height: bounds.height };
    }),
    // Connected combat rooms share an axis so even the widest lanes keep a
    // clean firing line. Unrelated bands remain independently staggered.
    connections: topology.edges,
    hallwayGaps: contract.hallwayLengths,
    passageWidth: Math.min(...contract.passageWidths),
    rng: layoutRng,
  });
  if (!layout.ok) {
    throw new Error(`packing:variable-footprints:${layout.error}`);
  }
  const roomRegions = new Map(layout.rooms.map((room) => [room.id, room.region]));
  const width = layout.width + MAP_MARGIN * 2;
  const height = layout.height + MAP_MARGIN * 2;
  const kitPalette = structureKit
    ? [
        structureKit.floor,
        ...[
          ...Object.values(structureKit.outerCorners),
          ...Object.values(structureKit.innerCorners),
          ...Object.values(structureKit.doorwayReturns),
          structureKit.post,
        ]
          .map((piece) => piece.voidUnderlay)
          .filter((tileName): tileName is string => tileName !== undefined),
      ]
    : null;
  const palette = [
    MAP_VOID_TILE,
    ...new Set(kitPalette ?? [
      ...FLOOR_TILES,
      ...moduleLibrary.flatMap((module) => module.land.map((tile) => tile.tileName)),
    ]),
  ];
  const paletteIndexByName = new Map(palette.map((tileName, index) => [tileName, index]));
  const structureFloorIndex = structureKit
    ? paletteIndexByName.get(structureKit.floor)
    : undefined;
  const cells = new Array<number>(width * height).fill(0);
  const blocked = new Array<boolean>(width * height).fill(true);
  let landmarkIndex = 0;
  const selections = new Map<
    string,
    { module: DungeonModuleDefinition; transform: DungeonModuleTransform; region: DungeonRegion }
  >();
  const rooms: DungeonRoom[] = topology.rooms.map((room) => {
    const role = roles.get(room.id)!;
    const { module, transform } = moduleSelection.assignments.get(room.id)!;
    const authoredRegion = roomRegions.get(room.id)!;
    const region: DungeonRegion = {
      x: MAP_MARGIN + authoredRegion.x,
      y: MAP_MARGIN + authoredRegion.y,
      width: authoredRegion.width,
      height: authoredRegion.height,
    };
    selections.set(room.id, { module, transform, region });
    const transformedFootprint = transformDungeonFootprint(module, transform);
    const transformedFootprintKeys = new Set(
      transformedFootprint.map((point) => key(point.x, point.y)),
    );
    const footprint = transformedFootprint.map((point) => globalPoint(region, point));
    for (const local of module.walkable) {
      const point = globalPoint(region, transformDungeonPoint(module, local, transform));
      blocked[point.y * width + point.x] = false;
    }
    for (const tile of module.land) {
      const local = transformDungeonPoint(module, tile.position, transform);
      if (module.footprint && !transformedFootprintKeys.has(key(local.x, local.y))) continue;
      const point = globalPoint(region, local);
      const tileName = structureKit?.floor ?? tile.tileName;
      const paletteIndex = paletteIndexByName.get(tileName);
      if (paletteIndex === undefined) throw new Error(`assembly:unknown-land-${tileName}`);
      cells[point.y * width + point.x] = paletteIndex;
    }
    const revealRegions = module.revealRegions.map((local) =>
      globalRegion(region, transformDungeonRegion(module, local, transform)),
    );
    const revealRegion = unionRegions(revealRegions);
    const landmark = role === 'landmark' ? `landmark-${landmarkIndex++}` : undefined;
    return {
      id: room.id,
      moduleId: module.id,
      role,
      grid: { x: room.x, y: room.y },
      region,
      revealRegion,
      revealRegions,
      footprint,
      clearanceHalo: transformDungeonPoints(
        module,
        module.clearanceHalo ?? deriveDungeonClearanceHalo(module),
        transform,
      ).map((point) => globalPoint(region, point)),
      sizeClass: module.sizeClass ?? dungeonRoomSizeClassForDimensions(module.width, module.height),
      reservedZones: module.reservedZones.map((zone) =>
        globalRegion(region, transformDungeonRegion(module, zone, transform)),
      ),
      encounterZones: (module.encounterZones ?? []).map((zone) =>
        globalRegion(region, transformDungeonRegion(module, zone, transform)),
      ),
      transform,
      ...(landmark ? { landmark } : {}),
    };
  });
  const topologyRoomById = new Map(topology.rooms.map((room) => [room.id, room]));
  const roomFootprintCells = rooms.flatMap((room) => room.footprint ?? []);
  const clearanceHaloCells = rooms.flatMap((room) => room.clearanceHalo ?? []);
  const reservedCells = rooms.flatMap((room) =>
    [...room.reservedZones, ...(room.encounterZones ?? [])].flatMap(cellsInRegion),
  );
  const existingCorridorFloor: TilePoint[] = [];
  const existingCorridorClearance: TilePoint[] = [];
  const connections: DungeonConnection[] = [];
  const passageWidths = shuffled(contract.passageWidths, decorationRng);
  for (const [index, edge] of topology.edges.entries()) {
    const preferredPassageWidth = passageWidths[index % passageWidths.length]!;
    const topologyA = topologyRoomById.get(edge.a)!;
    const topologyB = topologyRoomById.get(edge.b)!;
    const direction = directionBetween(topologyA, topologyB);
    const aSelection = selections.get(edge.a)!;
    const bSelection = selections.get(edge.b)!;
    const fromSockets = aSelection.module.sockets
      .map((socket) => transformedDungeonSocket(aSelection.module, socket, aSelection.transform))
      .filter((socket) => socket.direction === direction)
      .map((socket) => ({
        roomId: edge.a,
        socketId: socket.id,
        position: globalPoint(aSelection.region, socket.seam),
        direction: socket.direction,
        compatibility: socket.compatibility,
      }));
    const toSockets = bSelection.module.sockets
      .map((socket) => transformedDungeonSocket(bSelection.module, socket, bSelection.transform))
      .filter((socket) => socket.direction === opposite(direction))
      .map((socket) => ({
        roomId: edge.b,
        socketId: socket.id,
        position: globalPoint(bSelection.region, socket.seam),
        direction: socket.direction,
        compatibility: socket.compatibility,
      }));
    const widthCandidates = [...new Set(contract.passageWidths)]
      .filter((candidate) => candidate <= preferredPassageWidth)
      .sort((a, b) => b - a);
    let passageWidth = widthCandidates[0]!;
    let routed = routeDungeonCorridor({
      fromSockets,
      toSockets,
      passageWidth,
      bounds: { x: 0, y: 0, width, height },
      roomFootprintCells,
      clearanceHaloCells,
      reservedCells,
      existingCorridorFloor,
      existingCorridorClearance,
      rng: decorationRng,
    });
    for (const narrowerWidth of widthCandidates.slice(1)) {
      if (routed.ok) break;
      passageWidth = narrowerWidth;
      routed = routeDungeonCorridor({
        fromSockets,
        toSockets,
        passageWidth,
        bounds: { x: 0, y: 0, width, height },
        roomFootprintCells,
        clearanceHaloCells,
        reservedCells,
        existingCorridorFloor,
        existingCorridorClearance,
        rng: decorationRng,
      });
    }
    if (!routed.ok) {
      throw new Error(`packing:corridor-route-${edge.a}-${edge.b}-${routed.error}`);
    }
    const segments: DungeonCorridorSegment[] = routed.segments.map((segment, segmentIndex) => {
      const corridor = corridorAssignment(
        segment.direction,
        routed.from.compatibility,
        decorationRng,
        moduleLibrary,
      );
      if (!corridor) throw new Error(`packing:missing-corridor-${segment.direction}`);
      return {
        ...segment,
        moduleId: corridor.module.id,
        transform: corridor.transform,
        region: { ...routed.revealRegions[segmentIndex]! },
      };
    });
    for (const point of routed.floorCells) {
      const cell = point.y * width + point.x;
      blocked[cell] = false;
      cells[cell] = structureFloorIndex ?? floorCell(point.x, point.y, seed);
    }
    const revealRegions = routed.revealRegions.map((region) => ({ ...region }));
    const revealRegion = unionRegions(revealRegions);
    connections.push({
      id: `connection-${index}`,
      moduleId: segments[0]!.moduleId,
      passageWidth,
      region: revealRegion,
      transform: segments[0]!.transform,
      a: {
        roomId: edge.a,
        socketId: routed.from.socketId,
        direction: routed.from.direction,
        position: { ...routed.from.position },
        compatibility: routed.from.compatibility,
      },
      b: {
        roomId: edge.b,
        socketId: routed.to.socketId,
        direction: routed.to.direction,
        position: { ...routed.to.position },
        compatibility: routed.to.compatibility,
      },
      revealRegion,
      revealRegions,
      segments,
      footprint: routed.floorCells.map((point) => ({ ...point })),
      clearanceHalo: routed.clearanceCells.map((point) => ({ ...point })),
    });
    existingCorridorFloor.push(...routed.floorCells);
    existingCorridorClearance.push(...routed.clearanceCells);
  }

  const statics: StaticPlacement[] = [];
  const authoredPosts: TilePoint[] = [];
  const isAuthoredStructure = (item: DungeonModuleDefinition['statics'][number]): boolean =>
    Boolean(
      item.structureRole ||
      item.wallDirection ||
      (item.blocking && structureKit?.sourceTiles.includes(item.tileName)),
    );
  for (const room of rooms) {
    const selection = selections.get(room.id)!;
    for (const item of selection.module.statics) {
      if (item.structureRole?.kind === 'post') {
        const local = transformDungeonPoint(selection.module, item.position, selection.transform);
        authoredPosts.push(globalPoint(selection.region, local));
        continue;
      }
      if (isAuthoredStructure(item)) continue;
      const local = transformDungeonPoint(selection.module, item.position, selection.transform);
      const point = globalPoint(selection.region, local);
      statics.push({
        tileName: item.tileName,
        x: point.x,
        y: point.y,
        z: item.z,
        ...(item.light ? { light: structuredClone(item.light) } : {}),
        ...(item.lightTransparent ? { lightTransparent: true as const } : {}),
      });
      if (item.blocking) blocked[point.y * width + point.x] = true;
    }
  }
  for (const connection of connections) {
    for (const segment of connection.segments ?? []) {
      const corridor = moduleLibrary.find((module) => module.id === segment.moduleId);
      if (!corridor) throw new Error(`assembly:missing-corridor-module-${segment.moduleId}`);
      for (const item of corridor.statics) {
        if (isAuthoredStructure(item)) continue;
        const local = transformDungeonPoint(corridor, item.position, segment.transform);
        for (const point of repeatableHallwayPoints(corridor, local, segment)) {
          statics.push({
            tileName: item.tileName,
            x: point.x,
            y: point.y,
            z: item.z,
            ...(item.light ? { light: structuredClone(item.light) } : {}),
            ...(item.lightTransparent ? { lightTransparent: true as const } : {}),
          });
          if (item.blocking) blocked[point.y * width + point.x] = true;
        }
      }
    }
  }

  if (!structureKit) throw new Error('assembly:compiled-structure-requires-kit');
  const floorByKey = new Map<string, TilePoint>();
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!blocked[y * width + x]) floorByKey.set(key(x, y), { x, y });
    }
  }
  // A blocking prop can intentionally remove collision from semantic floor.
  // Restore exact authored floor before props are considered by the shell.
  for (const room of rooms) {
    for (const point of room.footprint ?? []) {
      floorByKey.set(key(point.x, point.y), point);
    }
  }
  for (const connection of connections) {
    for (const point of connection.footprint ?? []) {
      floorByKey.set(key(point.x, point.y), point);
    }
  }
  const compiled = compileDungeonStructures({
    floor: [...floorByKey.values()],
    apertures: connections.flatMap((connection) => [
      {
        position: connection.a.position,
        direction: connection.a.direction,
        width: connection.passageWidth,
      },
      {
        position: connection.b.position,
        direction: connection.b.direction,
        width: connection.passageWidth,
      },
    ]),
    posts: authoredPosts,
  });
  if (!compiled.ok) {
    throw new Error(`assembly:structure-compile:${compiled.errors.join(';')}`);
  }
  const identity: DungeonModuleTransform = { rotation: 0, mirrored: false };
  for (const placement of compiled.placements) {
    const piece = dungeonStructurePieceForRole(placement.role, identity, structureKit);
    const cell = placement.position.y * width + placement.position.x;
    applyStructureTerrain(piece, cells, cell, paletteIndexByName);
    statics.push({
      tileName: piece.tileName,
      x: placement.position.x,
      y: placement.position.y,
      z: 0,
    });
    blocked[cell] = true;
  }

  const markerOf = (roomId: string, kind: 'entrance' | 'exit' | 'landmark'): TilePoint => {
    const selection = selections.get(roomId)!;
    const marker = selection.module.markers.find((candidate) => candidate.kind === kind);
    if (!marker) throw new Error(`assembly:missing-${kind}-marker-${selection.module.id}`);
    return globalPoint(
      selection.region,
      transformDungeonPoint(selection.module, marker.position, selection.transform),
    );
  };
  const entrance = { roomId: topology.entranceId, position: markerOf(topology.entranceId, 'entrance') };
  const exit = { roomId: topology.exitId, position: markerOf(topology.exitId, 'exit') };
  const blueprint: DungeonBlueprint = {
    seed,
    generatorVersion: contract.generatorVersion,
    rooms,
    connections,
    entrance,
    exit,
    landmarks: rooms
      .filter((room) => room.role === 'landmark')
      .map((room) => ({ roomId: room.id, position: markerOf(room.id, 'landmark') })),
  };
  const routeDepth = entranceDistances.get(topology.exitId) ?? 0;
  const mainRoute = shortestPath(graph, topology.entranceId, topology.exitId);
  const mainRouteSet = new Set(mainRoute);
  const topologyRoomByMetricId = new Map(topology.rooms.map((room) => [room.id, room]));
  let mainRouteTurns = 0;
  let previousRouteAxis: 'horizontal' | 'vertical' | undefined;
  for (let index = 1; index < mainRoute.length; index++) {
    const previous = topologyRoomByMetricId.get(mainRoute[index - 1]!)!;
    const current = topologyRoomByMetricId.get(mainRoute[index]!)!;
    const axis = previous.x === current.x ? 'vertical' : 'horizontal';
    if (previousRouteAxis !== undefined && previousRouteAxis !== axis) mainRouteTurns += 1;
    previousRouteAxis = axis;
  }
  const sideDepths = deadEndDepths(graph, topology.entranceId, topology.exitId);
  const connectedPairs = new Set(topology.edges.map((edge) => edgeKey(edge.a, edge.b)));
  let unrelatedPairs = 0;
  let alignedUnrelatedPairs = 0;
  for (let aIndex = 0; aIndex < rooms.length; aIndex++) {
    const a = rooms[aIndex]!;
    for (let bIndex = aIndex + 1; bIndex < rooms.length; bIndex++) {
      const b = rooms[bIndex]!;
      if (connectedPairs.has(edgeKey(a.id, b.id))) continue;
      unrelatedPairs += 1;
      const aCenterX = a.region.x + (a.region.width - 1) / 2;
      const aCenterY = a.region.y + (a.region.height - 1) / 2;
      const bCenterX = b.region.x + (b.region.width - 1) / 2;
      const bCenterY = b.region.y + (b.region.height - 1) / 2;
      if (aCenterX === bCenterX || aCenterY === bCenterY) alignedUnrelatedPairs += 1;
    }
  }
  const unrelatedAxisAlignment = alignedUnrelatedPairs / Math.max(1, unrelatedPairs);
  if (unrelatedAxisAlignment > config.maxUnrelatedAxisAlignment) {
    throw new Error(`packing:unrelated-axis-alignment-${unrelatedAxisAlignment}`);
  }
  const moduleCounts = new Map<string, number>();
  for (const room of rooms) moduleCounts.set(room.moduleId, (moduleCounts.get(room.moduleId) ?? 0) + 1);
  const metrics: DungeonMetrics = {
    mainRouteDepth: routeDepth,
    mainRouteTurns,
    mainRouteBranchCount: mainRoute.filter((roomId) =>
      graph.get(roomId)!.some((neighbor) => !mainRouteSet.has(neighbor)),
    ).length,
    branchCount: rooms.filter((room) => graph.get(room.id)!.length >= 3).length,
    independentLoops: topology.edges.length - topology.rooms.length + 1,
    shortestLoopLength: shortestCycleLength(graph),
    deadEndCount: rooms.filter((room) =>
      room.id !== topology.entranceId &&
      room.id !== topology.exitId &&
      graph.get(room.id)!.length === 1,
    ).length,
    maxDeadEndDepth: Math.max(...sideDepths.values()),
    rewardedDeadEndCount: blueprint.landmarks.filter(
      (landmark) => (sideDepths.get(landmark.roomId) ?? 0) >= 2,
    ).length,
    minLandmarkSpacing: Math.min(
      ...blueprint.landmarks.flatMap((a, index) =>
        blueprint.landmarks
          .slice(index + 1)
          .map((b) => distances(graph, a.roomId).get(b.roomId) ?? 0),
      ),
    ),
    backtrackingBurden: [...sideDepths.values()].reduce(
      (total, depth) => total + depth * 2,
      0,
    ),
    packingBacktracks: topology.backtracks + moduleSelection.backtracks,
    moduleRepetition: Math.max(...moduleCounts.values()) / rooms.length,
    compactness: rooms.length / ((maxX - minX + 1) * (maxY - minY + 1)),
    unrelatedAxisAlignment,
  };
  const map: MapData = {
    width,
    height,
    palette,
    cells,
    blocked,
    statics,
    playerSpawn: entrance.position,
    dungeon: blueprint,
  };
  for (const connection of connections) {
    if (!contains(connection.revealRegion, connection.a.position)) {
      throw new Error(`connection ${connection.id} does not own its first socket`);
    }
  }
  const occupiedFootprints = new Set<string>();
  for (const room of rooms) {
    for (const point of room.footprint ?? []) {
      const cell = key(point.x, point.y);
      if (occupiedFootprints.has(cell)) throw new Error(`assembly:module-overlap-${room.id}`);
      occupiedFootprints.add(cell);
    }
  }
  const startKey = key(entrance.position.x, entrance.position.y);
  const reached = new Set<string>();
  const pending: TilePoint[] = [];
  if (!blocked[entrance.position.y * width + entrance.position.x]) {
    reached.add(startKey);
    pending.push(entrance.position);
  }
  while (pending.length > 0) {
    const current = pending.shift()!;
    for (const direction of DIRECTIONS) {
      const next = { x: current.x + direction.dx, y: current.y + direction.dy };
      const nextKey = key(next.x, next.y);
      if (
        next.x < 0 ||
        next.y < 0 ||
        next.x >= width ||
        next.y >= height ||
        reached.has(nextKey) ||
        blocked[next.y * width + next.x]
      ) {
        continue;
      }
      reached.add(nextKey);
      pending.push(next);
    }
  }
  if (!reached.has(key(exit.position.x, exit.position.y))) {
    throw new Error('assembly:entrance-exit-unreachable');
  }
  for (const connection of connections) {
    if (!reached.has(key(connection.a.position.x, connection.a.position.y))) {
      throw new Error(`assembly:unreachable-connection-${connection.id}`);
    }
  }
  for (const item of statics) {
    if (item.x < 0 || item.y < 0 || item.x >= width || item.y >= height) {
      throw new Error(`assembly:static-out-of-bounds-${item.tileName}`);
    }
  }
  if (new TextEncoder().encode(JSON.stringify(map)).byteLength > DUNGEON_MAX_MAP_PAYLOAD_BYTES) {
    throw new Error('assembly:payload-budget');
  }
  const renderPlacements = cells.filter((cell) => cell !== 0).length + statics.length;
  if (renderPlacements > DUNGEON_MAX_RENDER_PLACEMENTS) {
    throw new Error('assembly:render-budget');
  }
  return {
    ok: true,
    seed,
    generatorVersion: contract.generatorVersion,
    attempts,
    map,
    blueprint,
    metrics,
  };
}

/**
 * Generate one immutable-ready stone dungeon floor from an explicit seed.
 * Topology is selected before geometry, every retry has a deterministic derived
 * stream, and work is capped by `maxAttempts`. A caller receives either a complete
 * validated floor or a diagnostic failure naming the last violated constraint.
 */
function generateDungeonWithContract(
  options: GenerateDungeonCandidateOptions,
  moduleLibrary: readonly DungeonModuleDefinition[],
  contract: DungeonGenerationContract,
): GeneratedDungeon | DungeonGenerationFailure {
  const seed = options.seed >>> 0;
  const config = { ...DEFAULT_CONFIG, ...options.config };
  if (moduleLibrary.length === 0 || moduleLibrary.length > DUNGEON_MAX_MODULES) {
    return {
      ok: false,
      seed,
      generatorVersion: contract.generatorVersion,
      attempts: 0,
      constraint: `module-count-must-be-1-to-${DUNGEON_MAX_MODULES}`,
      stage: 'modules',
    };
  }
  if (
    !Number.isInteger(config.minRooms) ||
    !Number.isInteger(config.maxRooms) ||
    config.minRooms < DUNGEON_MIN_ROOMS ||
    config.maxRooms < config.minRooms ||
    config.maxRooms > DUNGEON_MAX_ROOMS ||
    !Number.isInteger(config.maxAttempts) ||
    config.maxAttempts < 1 ||
    !Number.isInteger(config.minLoops) ||
    config.minLoops < 0 ||
    !Number.isInteger(config.minLoopLength) ||
    config.minLoopLength < 4 ||
    !Number.isInteger(config.minMainRouteDepth) ||
    config.minMainRouteDepth < 1 ||
    !Number.isInteger(config.minMainRouteTurns) ||
    config.minMainRouteTurns < 0 ||
    !Number.isFinite(config.maxTopologyAspectRatio) ||
    config.maxTopologyAspectRatio < 1 ||
    !Number.isFinite(config.maxTopologyCompactness) ||
    config.maxTopologyCompactness <= 0 ||
    config.maxTopologyCompactness > 1 ||
    !Number.isInteger(config.minBranches) ||
    config.minBranches < 0 ||
    !Number.isInteger(config.minDeadEnds) ||
    config.minDeadEnds < 0 ||
    !Number.isInteger(config.minDeadEndDepth) ||
    config.minDeadEndDepth < 1 ||
    !Number.isInteger(config.minRewardedDeadEnds) ||
    config.minRewardedDeadEnds < 0 ||
    !Number.isFinite(config.maxUnrelatedAxisAlignment) ||
    config.maxUnrelatedAxisAlignment < 0 ||
    config.maxUnrelatedAxisAlignment > 1
  ) {
    return {
      ok: false,
      seed,
      generatorVersion: contract.generatorVersion,
      attempts: 0,
      constraint: 'invalid-generation-config',
      stage: 'config',
    };
  }

  const invalidModule = [...new Set(moduleLibrary)]
    .map((module) => ({ module, validation: validateDungeonModule(module) }))
    .find(({ validation }) => !validation.ok);
  if (invalidModule) {
    return {
      ok: false,
      seed,
      generatorVersion: contract.generatorVersion,
      attempts: 0,
      constraint: `invalid-module:${invalidModule.module.id}`,
      stage: 'modules',
      diagnostics: { errors: invalidModule.validation.errors.join('; ') },
    };
  }
  const requiredRoles: DungeonRoomRole[] = [
    'entrance',
    'exit',
    'landmark',
    'junction',
    'dead-end',
    'ordinary',
  ];
  const missingRole = requiredRoles.find(
    (role) => !moduleLibrary.some((module) => module.roles.includes(role)),
  );
  if (missingRole) {
    return {
      ok: false,
      seed,
      generatorVersion: contract.generatorVersion,
      attempts: 0,
      constraint: `no-module-for-role:${missingRole}`,
      stage: 'modules',
    };
  }

  let constraint = 'generation-attempts-exhausted';
  let stage: DungeonGenerationFailure['stage'] = 'topology';
  let backtracks = 0;
  for (let attempt = 0; attempt < config.maxAttempts; attempt++) {
    const rng = new SeededRng(mixedSeed(seed, attempt));
    const roomCount = config.minRooms + rng.nextInt(config.maxRooms - config.minRooms + 1);
    const topology = planDungeonTopology(roomCount, rng);
    constraint = topologyFailure(topology, config) ?? '';
    if (constraint !== '') continue;
    stage = 'packing';
    const packed = packTopology(topology, rng);
    if (!packed) {
      constraint = 'packing-backtrack-budget-exhausted';
      backtracks = PACKING_MAX_BACKTRACKS;
      continue;
    }
    backtracks = packed.backtracks;
    constraint = packedTopologyFailure(packed, config) ?? '';
    if (constraint !== '') continue;
    try {
      return assemble(packed, seed, attempt + 1, moduleLibrary, contract, config);
    } catch (error) {
      constraint = error instanceof Error ? error.message : String(error);
      stage = constraint.startsWith('packing:') ? 'packing' : 'assembly';
    }
  }
  return {
    ok: false,
    seed,
    generatorVersion: contract.generatorVersion,
    attempts: config.maxAttempts,
    constraint,
    stage,
    diagnostics: { backtracks, backtrackBudget: PACKING_MAX_BACKTRACKS },
  };
}

export const DUNGEON_GENERATOR_VERSIONS: readonly number[] = Object.freeze([
  DUNGEON_GENERATOR_VERSION,
]);

export function generateDungeon(
  options: GenerateDungeonOptions,
): GeneratedDungeon | DungeonGenerationFailure {
  const generatorVersion = options.generatorVersion ?? DUNGEON_GENERATOR_VERSION;
  if (generatorVersion !== DUNGEON_GENERATOR_VERSION) {
    return {
      ok: false,
      seed: options.seed >>> 0,
      generatorVersion,
      attempts: 0,
      constraint: `unsupported-generator-version:${generatorVersion}`,
      stage: 'config',
    };
  }
  return generateDungeonWithContract(
    { seed: options.seed, generatorVersion },
    DUNGEON_MODULES,
    CURRENT_DUNGEON_CONTRACT,
  );
}

/**
 * Exercise an editor-authored candidate corpus through the bounded current assembler.
 *
 * This authoring seam is separate from {@link generateDungeon}: a candidate
 * library cannot replace the frozen production module corpus.
 */
export function generateDungeonCandidate(
  options: GenerateDungeonCandidateOptions,
): GeneratedDungeon | DungeonGenerationFailure {
  const generatorVersion = options.generatorVersion ?? DUNGEON_GENERATOR_VERSION;
  if (generatorVersion !== DUNGEON_GENERATOR_VERSION) {
    return {
      ok: false,
      seed: options.seed >>> 0,
      generatorVersion,
      attempts: 0,
      constraint: `unsupported-generator-version:${generatorVersion}`,
      stage: 'config',
    };
  }
  const contract = options.structureKit
    ? {
        ...CURRENT_DUNGEON_CONTRACT,
        structureKitId: undefined,
        structureKit: defineDungeonStructureKit(options.structureKit),
      }
    : CURRENT_DUNGEON_CONTRACT;
  const moduleLibrary = options.moduleLibrary ?? DUNGEON_MODULES;
  return generateDungeonWithContract(options, moduleLibrary, contract);
}
